import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';

export default function SubmitIdea({ onSubmit }) {
  const { user, setUser } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  if (!user) return (
    <div style={styles.loginPrompt}>
      💡 Have a training topic idea? <strong>Register or log in</strong> to submit it.
    </div>
  );

  if (user.post_credits <= 0) return (
    <div style={styles.noCredits}>
      ✏️ You've used all your post credits for this week. You'll get 2 more next Monday.
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return setError('Title is required');
    setLoading(true);
    setError('');
    try {
      await api.post('/ideas', { title: title.trim(), description: description.trim() });

      // Refresh user credits
      const userRes = await api.get('/auth/me');
      setUser(userRes.data);

      setTitle('');
      setDescription('');
      setOpen(false);
      onSubmit();
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      {!open ? (
        <button onClick={() => setOpen(true)} style={styles.openBtn}>
          💡 Submit a training idea
          <span style={styles.creditBadge}>{user.post_credits} left</span>
        </button>
      ) : (
        <form onSubmit={handleSubmit} style={styles.form}>
          <h3 style={styles.heading}>Submit a training idea</h3>

          <label style={styles.label}>Title *</label>
          <input
            type="text"
            placeholder="e.g. How to configure SIP trunks on OmniPCX"
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={255}
            required
            style={styles.input}
          />
          <div style={styles.charCount}>{title.length}/255</div>

          <label style={styles.label}>Description (optional)</label>
          <textarea
            placeholder="Add more detail about what you'd like covered..."
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={3}
            style={styles.textarea}
          />

          {error && <p style={styles.error}>{error}</p>}

          <div style={styles.actions}>
            <button
              type="button"
              onClick={() => { setOpen(false); setError(''); }}
              style={styles.cancelBtn}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={styles.submitBtn}
            >
              {loading ? 'Submitting...' : `Submit (${user.post_credits} credit${user.post_credits !== 1 ? 's' : ''} left)`}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

const styles = {
  wrapper: {
    marginBottom: '24px',
  },
  openBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 20px',
    borderRadius: '10px',
    border: '2px dashed #d1d5db',
    background: '#f9fafb',
    color: '#374151',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    width: '100%',
    justifyContent: 'center',
    transition: 'all 0.15s',
  },
  creditBadge: {
    background: '#dbeafe',
    color: '#2563eb',
    fontSize: '12px',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '20px',
  },
  form: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    padding: '24px',
  },
  heading: {
    margin: '0 0 20px 0',
    fontSize: '16px',
    fontWeight: '700',
    color: '#111827',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  charCount: {
    fontSize: '12px',
    color: '#9ca3af',
    textAlign: 'right',
    marginBottom: '16px',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box',
    marginBottom: '16px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  cancelBtn: {
    padding: '10px 20px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    background: '#ffffff',
    color: '#374151',
    fontSize: '14px',
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '10px 20px',
    borderRadius: '6px',
    border: 'none',
    background: '#2563eb',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  loginPrompt: {
    padding: '16px 20px',
    borderRadius: '10px',
    border: '2px dashed #d1d5db',
    background: '#f9fafb',
    color: '#6b7280',
    fontSize: '14px',
    textAlign: 'center',
    marginBottom: '24px',
  },
  noCredits: {
    padding: '16px 20px',
    borderRadius: '10px',
    background: '#fef9c3',
    color: '#854d0e',
    fontSize: '14px',
    textAlign: 'center',
    marginBottom: '24px',
  },
  error: {
    color: '#ef4444',
    fontSize: '13px',
    marginBottom: '12px',
  },
};